import * as React from 'react';

import { CurrencyCode } from '@rematter/frontend-lib-api-client';
import { Button, DataGridColumn, Menu, Money, Tooltip } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import {
  LucideCircleOff,
  LucideClipboardCheck,
  LucideEllipsis,
  LucideEye,
  LucideEyeOff,
  LucideFileDigit,
  LucideLink2,
  LucideLink2Off,
  LucidePencil,
} from 'lucide-react';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { FinancialAccountRecord } from '~/modules/finance/types';
import { getLocale } from '~/shared/utils/locale';

type UseFinancialAccountListColumnsArgs = {
  onEditAccount: (account: FinancialAccountRecord) => void;
  onViewTransactions: (account: FinancialAccountRecord) => void;
  onReconcileAccount: (account: FinancialAccountRecord) => void;
};

export const useFinancialAccountListColumns = ({
  onEditAccount,
  onViewTransactions,
  onReconcileAccount,
}: UseFinancialAccountListColumnsArgs): DataGridColumn<FinancialAccountRecord>[] => {
  const t = useTranslations();

  return React.useMemo(
    () => [
      {
        key: 'name',
        header: t.financialAccounts.form.fields.accountName,
        width: 200,
        cell: (cell) => (
          <span className="truncate font-medium" title={cell.row.name}>
            {cell.row.name}
          </span>
        ),
      },
      {
        key: 'status',
        header: '',
        width: 70,
        cell: (cell) => (
          <div className="flex items-center gap-1">
            <Tooltip
              tip={
                cell.row.linked
                  ? t.financialAccounts.list.tooltips.linked
                  : t.financialAccounts.list.tooltips.notLinked
              }
            >
              {cell.row.linked ? (
                <LucideLink2 className="size-4 text-success/80" />
              ) : (
                <LucideLink2Off className="size-4 text-neutral/60" />
              )}
            </Tooltip>
            <Tooltip
              tip={
                cell.row.visibleInPayments
                  ? t.financialAccounts.list.tooltips.visibleInPayments
                  : t.financialAccounts.list.tooltips.hiddenFromPayments
              }
            >
              {cell.row.visibleInPayments ? (
                <LucideEye className="size-4 text-success/80" />
              ) : (
                <LucideEyeOff className="size-4 text-failure/80" />
              )}
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'type',
        header: t.financialAccounts.form.fields.accountType,
        width: 120,
        cell: (cell) => <span>{cell.row.type}</span>,
      },
      {
        key: 'facility',
        header: t.financialAccounts.form.fields.facility,
        width: 150,
        cell: (cell) => (
          <span className="truncate" title={cell.row.facilityName}>
            {cell.row.facilityName ?? '-'}
          </span>
        ),
      },
      {
        key: 'currentBalance',
        header: t.financialAccounts.form.fields.initialBalance,
        width: 150,
        align: 'right',
        cell: (cell) => (
          <div
            className={cn(
              'text-right font-semibold',
              cell.row.currentBalance.amount < 0
                ? 'text-failure/80'
                : 'text-success/80',
            )}
          >
            <Money
              amount={cell.row.currentBalance.amount}
              currencyCode={cell.row.currency as CurrencyCode}
            />
          </div>
        ),
      },
      {
        key: 'updatedAt',
        header: t.financialAccounts.list.status.lastUpdate,
        width: 130,
        cell: (cell) => (
          <span className="text-neutral/60">
            {cell.row.updatedAt instanceof Date &&
            !isNaN(cell.row.updatedAt.getTime())
              ? cell.row.updatedAt.toLocaleDateString(getLocale(), {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : '-'}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: (cell) => (
          <div className="flex justify-center">
            <Menu
              placement="bottom end"
              menuClassName="min-w-36"
              trigger={
                <Button
                  size="icon"
                  icon={<LucideEllipsis />}
                  variant="outline"
                />
              }
              items={[
                {
                  label: t.financialAccounts.list.buttons.edit,
                  icon: <LucidePencil size={14} />,
                  onAction: () => onEditAccount(cell.row),
                },
                {
                  label: t.financialAccounts.list.viewTransactions,
                  icon: <LucideFileDigit size={14} />,
                  onAction: () => onViewTransactions(cell.row),
                },
                ...(cell.row.type === 'Cash'
                  ? [
                      {
                        label: t.financialAccounts.list.reconcile,
                        icon: <LucideClipboardCheck size={14} />,
                        onAction: () => onReconcileAccount(cell.row),
                      },
                    ]
                  : []),
                {
                  label: t.financialAccounts.list.viewDisable,
                  icon: <LucideCircleOff size={14} />,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t, onEditAccount, onViewTransactions, onReconcileAccount],
  );
};
